import { Link, useParams } from 'react-router-dom';
import { TierBox, TIER_LABEL } from '@/components/TierBox';
import { Activity, AlertTriangle, ArrowLeft, AtSign, BookText, ShieldAlert, Cloud, DatabaseBackup, Loader2, MonitorSmartphone, Network, Receipt, Ticket } from 'lucide-react';
import { useCompanyOrbit, type CompanyOrbit, type OrbitHealth } from '@/api/insights';
import { PageHeader } from '@/components/PageHeader';
import { DesktopOnly } from '@/components/dashboard/DesktopOnly';
import { fmtAge } from '@/lib/rmm-helpers';
import { fmtRelative } from '@/lib/cobalt-helpers';
import {
  ROUTES, backupSaasCustomerPath, docsOrgPath, cippTenantPath, cspLicencesView, domainPath, insightsCoverageView, insightsDevicePath, insightsGapsView, rmmAlertsView, rmmClientSitesPath, rmmDevicePath, rmmDevicesView, socIncidentPath, ticketPath,
} from '@/lib/routes';
import { Journey } from '@/components/domains/DomainsKit';
import { cn } from '@/lib/utils';

/**
 * Orbit — one Autotask company across every module (§4.2): an at-a-glance strip, one card per
 * product with its health (RMM patching/alerts, M365 posture, backup coverage, CSP renewals in 60
 * days, SOC), the client's gaps, worst RMM alerts, device coverage, open tickets and the last 30 days
 * of ticket activity. Everything links into the module page pre-filtered.
 */
export default function OrbitPage() {
  const id = Number(useParams<{ id: string }>().id);
  const { data, isLoading, error, isFetching } = useCompanyOrbit(Number.isFinite(id) ? id : undefined);
  const c = data?.company;
  const h: OrbitHealth = data?.health ?? { rmm: null, security: null, backup: null, csp: null, soc: null, gaps: null, activity: null };

  return (
    <DesktopOnly title="Orbit is available on desktop" message="Open Cosmos on a larger screen to view a client.">
      <div className="mx-auto w-full max-w-[1400px] px-4 py-4 md:px-6">
        <Link to={ROUTES.companies} className="mb-2 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-3 w-3" /> Companies
        </Link>
        {isLoading ? (
          <div className="flex items-center gap-2 py-16 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Gathering this client from every module…</div>
        ) : error ? (
          <div className="mt-4 text-sm text-destructive">{error instanceof Error ? error.message : 'Failed to load'}</div>
        ) : data && c ? (
          <>
            <PageHeader title={c.name} isFetching={isFetching}
              subtitle={<><span className="inline-flex items-center gap-1.5 align-middle">{c.tier && <><TierBox tier={c.tier} /><span className="font-medium text-foreground">{TIER_LABEL[c.tier]}</span> · </>}</span><span className="font-medium text-brand">Orbit</span> · Autotask company #{c.id} · linked in {c.products.length} of {Object.values(data.available).filter(Boolean).length} products</>} />

            <Glance data={data} />

            <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
              <ProductCard icon={MonitorSmartphone} title="Datto RMM" linked={!!c.rmm} available={data.available.rmm}>
                {c.rmm && <>
                  <Stat value={c.rmm.devices} label="devices" />
                  <div className="mt-1 text-xs text-muted-foreground">{c.rmm.sites.length} site{c.rmm.sites.length === 1 ? '' : 's'}: {c.rmm.sites.map((s) => s.name).join(', ')}</div>
                  {h.rmm && (
                    <div className="mt-2 space-y-0.5 text-xs">
                      <Line label="Online now" value={`${h.rmm.online} of ${h.rmm.devices}`} />
                      <Line label="Patch compliance" value={h.rmm.patchPct != null ? `${h.rmm.patchPct}%` : '—'} tone={h.rmm.patchPct != null && h.rmm.patchPct < 75 ? 'bad' : h.rmm.patchPct != null && h.rmm.patchPct < 90 ? 'warn' : undefined} />
                      <Line label="Open alerts" value={h.rmm.openAlerts} tone={h.rmm.openAlerts ? 'warn' : undefined} />
                      {h.rmm.stale30 > 0 && <Line label="Offline 30+ days" value={h.rmm.stale30} tone="warn" />}
                      {h.rmm.reboot > 0 && <Line label="Reboot required" value={h.rmm.reboot} />}
                      {h.rmm.avIssues > 0 && <Line label="Antivirus issues" value={h.rmm.avIssues} tone="warn" />}
                    </div>
                  )}
                  <CardLinks links={[{ to: rmmDevicesView({ client: c.name }), label: 'Devices' }, { to: rmmClientSitesPath(c.id), label: 'Sites' }, { to: `${ROUTES.rmm.dashboards.patching}?scope=${c.id}`, label: 'Patching' }]} />
                </>}
              </ProductCard>
              <ProductCard icon={Cloud} title="Microsoft 365" linked={!!c.cipp} available={data.available.cipp}>
                {c.cipp && <>
                  <div className="text-sm font-medium text-foreground">{c.cipp.displayName}</div>
                  <div className="text-xs text-muted-foreground">{c.cipp.defaultDomainName}</div>
                  {h.security && (h.security.building ? <div className="mt-2 text-xs text-muted-foreground">Security posture is still loading…</div> : (
                    <div className="mt-2 space-y-0.5 text-xs">
                      <Line label="Secure score" value={h.security.secureScorePct != null ? `${h.security.secureScorePct}%` : '—'} tone={h.security.secureScorePct != null && h.security.secureScorePct < 40 ? 'bad' : undefined} />
                      <Line label="MFA registered" value={h.security.mfaUnknown ? 'unknown · no P1' : h.security.mfaRegisteredPct != null ? `${h.security.mfaRegisteredPct}%` : '—'} />
                      <Line label="MFA enforced by" value={h.security.enforcement ?? '—'} tone={h.security.enforcement === 'None' ? 'bad' : h.security.enforcement === 'Partial' || h.security.enforcement === 'Mixed' ? 'warn' : undefined} />
                      {!!h.security.adminsNoMfa && <Line label="Admins without MFA" value={h.security.adminsNoMfa} tone="bad" />}
                      {!!h.security.forwarding && <Line label="External forwarding" value={h.security.forwarding} tone="warn" />}
                      {h.security.issues.length > 0 && (
                        <ul className="mt-1 space-y-0.5">
                          {h.security.issues.slice(0, 4).map((i) => <li key={i.label} className={i.severity === 'critical' ? 'text-destructive' : 'text-warning'}>• {i.label}</li>)}
                        </ul>
                      )}
                    </div>
                  ))}
                  <CardLinks links={[{ to: cippTenantPath(c.cipp.customerId), label: 'Users & security' }, { to: `${ROUTES.cipp.dashboards.security}?scope=${c.id}`, label: 'Security posture' }]} />
                </>}
              </ProductCard>
              <ProductCard icon={Receipt} title="CSP licences" linked={!!c.csp} available={data.available.csp}>
                {c.csp && <>
                  <Stat value={c.csp.subscriptions} label="subscriptions" />
                  {h.csp && (h.csp.renewals.length === 0 ? <div className="mt-1 text-xs text-muted-foreground">No renewals in the next 60 days.</div> : (
                    <ul className="mt-2 space-y-0.5 text-xs">
                      <li className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Renewing in 60 days</li>
                      {h.csp.renewals.slice(0, 5).map((r, i) => (
                        <li key={i} className="flex items-center justify-between gap-2">
                          <span className="truncate text-foreground" title={r.plan}>{r.quantity} × {r.plan}</span>
                          <span className={cn('shrink-0', r.days < 0 ? 'text-destructive' : r.days <= 7 ? 'text-warning' : 'text-muted-foreground')}>
                            {r.days < 0 ? `${-r.days} d ago` : r.days === 0 ? 'today' : `${r.days} d`}{!r.autoRenew && ' · no auto-renew'}
                          </span>
                        </li>
                      ))}
                      {h.csp.renewals.length > 5 && <li className="text-muted-foreground">+ {h.csp.renewals.length - 5} more</li>}
                    </ul>
                  ))}
                  <CardLinks links={c.csp.customers.map((n) => ({ to: cspLicencesView({ client: n }), label: c.csp!.customers.length > 1 ? n : 'Licences & renewals' }))} />
                </>}
              </ProductCard>
              <ProductCard icon={DatabaseBackup} title="SaaS backup" linked={!!c.backup?.length} available={data.available.backup}>
                {c.backup?.map((b) => (
                  <div key={b.saasCustomerId} className="flex items-center justify-between gap-2 text-sm">
                    <Link to={backupSaasCustomerPath(b.saasCustomerId)} className="truncate text-foreground hover:underline">{b.name}</Link>
                    <span className={cn('shrink-0 whitespace-nowrap text-xs font-medium', b.health === 'healthy' ? 'text-success' : b.health === 'failing' ? 'text-destructive' : b.health === 'warning' ? 'text-warning' : 'text-muted-foreground')}>
                      {b.health === 'warning' ? 'issues' : b.health}{b.seats != null && <span className="text-muted-foreground"> · {b.seats} seats</span>}
                    </span>
                  </div>
                ))}
                {h.backup?.map((b) => (b.coveragePct != null || b.notProtected) ? (
                  <div key={`cov-${b.saasCustomerId}`} className="mt-1 space-y-0.5 text-xs">
                    {b.coveragePct != null && <Line label="M365 users protected" value={`${b.coveragePct}%`} tone={b.coveragePct < 95 ? 'warn' : undefined} />}
                    {!!b.notProtected && <Line label="Licensed, not backed up" value={b.notProtected} tone="bad" />}
                    {!!b.leavers && <Line label="Leavers still protected" value={b.leavers} />}
                  </div>
                ) : null)}
              </ProductCard>
              <ProductCard icon={Network} title="Network" linked={!!(data.network && (data.network.firewalls.length || data.network.sites.length))} available={data.available.network}>
                {data.network && <>
                  {data.network.firewalls.map((f) => (
                    <div key={f.id} className="flex items-center justify-between gap-2 text-sm">
                      <span className="truncate text-foreground" title={f.model ?? ''}>{f.hostname}</span>
                      <span className={cn('shrink-0 text-xs font-medium', !f.connected || f.subscriptionState === 'expired' ? 'text-destructive' : f.subscriptionState === 'expiring' || f.openAlerts ? 'text-warning' : 'text-success')}>
                        {!f.connected ? 'disconnected' : f.subscriptionState === 'expired' ? 'subscription expired' : f.subscriptionState === 'expiring' ? `renews ${f.subscriptionEnd}` : f.openAlerts ? `${f.openAlerts} alert${f.openAlerts === 1 ? '' : 's'}` : 'ok'}
                      </span>
                    </div>
                  ))}
                  {data.network.sites.map((s) => (
                    <div key={s.id} className="flex items-center justify-between gap-2 text-sm">
                      <span className="truncate text-foreground">{s.label}</span>
                      <span className={cn('shrink-0 text-xs font-medium', !s.hostConnected ? 'text-destructive' : s.offline || s.internetIssues ? 'text-warning' : 'text-success')}>
                        {!s.hostConnected ? 'console disconnected' : s.offline ? `${s.offline} of ${s.devices} offline` : s.internetIssues ? 'WAN issues' : `${s.devices} devices ok`}
                      </span>
                    </div>
                  ))}
                  <CardLinks links={[
                    ...(data.network.firewalls.length ? [{ to: ROUTES.network.sophos.firewalls, label: 'Sophos firewalls' }] : []),
                    ...(data.network.sites.length ? [{ to: ROUTES.network.unifi.sites, label: 'UniFi sites' }] : []),
                  ]} />
                </>}
              </ProductCard>
              <ProductCard icon={AtSign} title="Domains" linked={!!(data.domains && (data.domains.items.length || data.domains.unmonitored.length))} available={data.available.domains}>
                {data.domains && <>
                  {data.domains.items.map((d) => (
                    <Link key={d.domain} to={domainPath(d.domain)} className="flex items-center justify-between gap-2 rounded text-sm hover:bg-muted/40">
                      <span className="truncate text-foreground">{d.domain}</span>
                      <span className="flex shrink-0 items-center gap-2">
                        {d.readyToEnforce && <span className="text-[11px] font-semibold text-warning">ready to tighten</span>}
                        <Journey stage={d.stage} showLabel={false} />
                      </span>
                    </Link>
                  ))}
                  {data.domains.unmonitored.length > 0 && (
                    <div className="mt-1 text-xs text-warning" title={data.domains.unmonitored.join(', ')}>
                      {data.domains.unmonitored.length} mail domain{data.domains.unmonitored.length === 1 ? '' : 's'} not in dmarcian
                    </div>
                  )}
                </>}
              </ProductCard>
              <ProductCard icon={BookText} title="Docs (IT Glue)" linked={!!data.docs} available={data.available.docs}>
                {data.docs && <>
                  <Link to={docsOrgPath(data.docs.orgId)} className="text-sm font-medium text-foreground hover:underline">{data.docs.orgName}</Link>
                  {data.docs.alert && <div className="mt-1 rounded bg-warning-soft px-2 py-1 text-xs text-warning">{data.docs.alert}</div>}
                  {data.docs.quickNotes && <p className="mt-1 line-clamp-3 whitespace-pre-line text-xs text-muted-foreground">{data.docs.quickNotes}</p>}
                  <div className="mt-2 flex gap-4">
                    <Stat value={data.docs.configurations} label="configurations" />
                    <Stat value={data.docs.expiringSoon} label="expiring in 90d" />
                  </div>
                  {data.docs.notInRmm > 0 && <div className="mt-1 text-xs text-warning">{data.docs.notInRmm} computer{data.docs.notInRmm === 1 ? '' : 's'} in IT Glue without a Datto RMM device</div>}
                  <CardLinks links={[
                    { to: docsOrgPath(data.docs.orgId), label: 'Documents' },
                    { to: docsOrgPath(data.docs.orgId, 'configurations'), label: 'Configurations' },
                    { to: docsOrgPath(data.docs.orgId, 'expirations'), label: 'Expirations' },
                  ]} />
                </>}
              </ProductCard>
              <ProductCard icon={ShieldAlert} title="SOC (Huntress)" linked={!!c.soc} available={data.available.soc}>
                {c.soc && <>
                  <div className="flex gap-4">
                    <Stat value={c.soc.agents} label="agents reporting" />
                    <Stat value={c.soc.openIncidents} label="open incidents" />
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">{c.soc.orgs.map((o) => o.name).join(', ')}</div>
                  {h.soc && (
                    <div className="mt-2 space-y-0.5 text-xs">
                      {h.soc.silent > 0 && <Line label="Not checking in" value={h.soc.silent} tone="warn" />}
                      {h.soc.defender > 0 && <Line label="Defender unhealthy" value={h.soc.defender} tone="warn" />}
                      {h.soc.stale > 0 && <Line label="Stale agents" value={h.soc.stale} />}
                      <Line label="Incidents · 90 days" value={h.soc.incidents90} />
                      {h.soc.open.map((i) => <Link key={i.id} to={socIncidentPath(i.id)} className="block truncate text-warning hover:underline" title={i.summary}>• {i.summary}</Link>)}
                    </div>
                  )}
                  <CardLinks links={[
                    // ?scope= sets the header client picker, so the SOC pages open filtered to this client.
                    { to: `${ROUTES.soc.root}?scope=${c.id}`, label: 'SOC overview' },
                    { to: `${ROUTES.soc.incidents}?scope=${c.id}`, label: 'Incidents' },
                    { to: `${ROUTES.soc.agents}?scope=${c.id}`, label: 'Agents' },
                    { to: insightsGapsView({ kind: 'no-edr', q: c.name }), label: 'Without Huntress' },
                  ]} />
                </>}
              </ProductCard>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_420px]">
              <CoverageSection data={data} />
              <TicketsSection data={data} />
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
              <GapsSection health={h} companyName={c.name} />
              <AlertsSection health={h} companyName={c.name} />
              <ActivitySection health={h} />
            </div>
          </>
        ) : null}
      </div>
    </DesktopOnly>
  );
}

function ProductCard({ icon: Icon, title, linked, available, children }: { icon: typeof Cloud; title: string; linked: boolean; available: boolean; children: React.ReactNode }) {
  return (
    <section className={cn('rounded-lg border bg-card p-4 shadow-sm', linked ? 'border-border' : 'border-dashed border-border')}>
      <div className="mb-2 flex items-center gap-2">
        <span className={cn('flex h-7 w-7 items-center justify-center rounded-md', linked ? 'bg-brand-soft text-brand' : 'bg-muted text-muted-foreground')}><Icon className="h-3.5 w-3.5" /></span>
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      </div>
      {linked ? children : <div className="text-xs text-muted-foreground">{available ? 'Not linked to this company' : 'Not configured in Cosmos'}</div>}
    </section>
  );
}

function Stat({ value, label }: { value: number; label: string }) {
  return <div><span className="text-2xl font-semibold tabular-nums text-foreground">{value}</span> <span className="text-xs text-muted-foreground">{label}</span></div>;
}

function CardLinks({ links }: { links: { to: string; label: string }[] }) {
  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {links.map((l) => <Link key={l.to} to={l.to} className="rounded-md border border-border px-2 py-0.5 text-xs text-muted-foreground hover:border-ring hover:text-foreground">{l.label} →</Link>)}
    </div>
  );
}

function CoverageSection({ data }: { data: CompanyOrbit }) {
  const s = data.coverage.summary;
  const gaps = data.coverage.devices;
  return (
    <section className="rounded-lg border border-border bg-card shadow-sm">
      <div className="flex items-center justify-between gap-3 border-b border-divider px-4 py-2.5">
        <h2 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Device coverage · Datto RMM × Intune</h2>
        <span className="flex items-center gap-3">
          <Link to={insightsGapsView({ q: data.company.name })} className="text-xs text-muted-foreground hover:text-foreground hover:underline">Gap list &amp; tickets →</Link>
          <Link to={insightsCoverageView({ state: 'gaps' })} className="text-xs text-muted-foreground hover:text-foreground hover:underline">All clients →</Link>
        </span>
      </div>
      {!s ? <div className="px-4 py-5 text-xs text-muted-foreground">No devices for this company in Datto RMM or Intune.</div> : (
        <>
          <div className="grid grid-cols-2 gap-3 px-4 py-3 sm:grid-cols-4">
            <Mini label="In both" value={s.pct != null ? `${s.pct}%` : 'n/a'} sub={s.pct == null ? `uses ${[s.usesRmm && 'RMM', s.usesIntune && 'Intune'].filter(Boolean).join(' + ') || 'neither'}` : `${s.inBoth} devices`} />
            <Mini label="Not in RMM" value={s.intuneOnly} bad={s.intuneOnly > 0} />
            <Mini label="Not in Intune" value={s.rmmOnly} warn={s.rmmOnly > 0} />
            <Mini label="Check-in drift" value={s.drift} warn={s.drift > 0} />
          </div>
          {gaps.length === 0 ? <div className="border-t border-divider px-4 py-4 text-xs text-success">No gaps or drift for this client.</div> : (
            <div className="max-h-96 overflow-y-auto border-t border-divider">
              <table className="w-full text-sm">
                <tbody className="divide-y divide-divider">
                  {gaps.map((d) => (
                    <tr key={d.key}>
                      <td className="px-4 py-2">
                        <Link to={insightsDevicePath(d.key)} className="font-medium text-foreground hover:underline">{d.hostname}</Link>
                        <div className="text-[11px] text-muted-foreground">{d.os ?? '—'}{d.intune?.user && ` · ${d.intune.user}`}</div>
                      </td>
                      <td className="px-4 py-2 text-xs">
                        {d.state === 'intune-only' && <span className="text-destructive">In Intune, no Datto RMM agent</span>}
                        {d.state === 'rmm-only' && <span className="text-warning">In Datto RMM, not enrolled in Intune</span>}
                        {d.state === 'drift' && <span className="text-warning">RMM {d.rmm?.online ? 'online' : `seen ${fmtAge(d.rmm?.lastSeen ?? null)} ago`} · Intune synced {fmtAge(d.intune?.lastSync ?? null)} ago</span>}
                        {d.flags.map((f) => <div key={f} className="text-warning">{f}</div>)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  );
}

function Mini({ label, value, sub, bad, warn }: { label: string; value: string | number; sub?: string; bad?: boolean; warn?: boolean }) {
  return (
    <div>
      <div className="text-[10.5px] font-medium uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={cn('text-xl font-semibold tabular-nums', bad ? 'text-destructive' : warn ? 'text-amber-600 dark:text-amber-400' : 'text-foreground')}>{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground">{sub}</div>}
    </div>
  );
}

function TicketsSection({ data }: { data: CompanyOrbit }) {
  const t = data.tickets;
  return (
    <section className="rounded-lg border border-border bg-card shadow-sm">
      <div className="flex items-center gap-2 border-b border-divider px-4 py-2.5">
        <Ticket className="h-3.5 w-3.5 text-muted-foreground" />
        <h2 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Open tickets{t.total != null && ` · ${t.total >= 100 ? '100+' : t.total}`}</h2>
      </div>
      {t.error ? <div className="px-4 py-4 text-xs text-destructive">Couldn't load tickets: {t.error}</div>
        : t.items.length === 0 ? <div className="px-4 py-4 text-xs text-success">No open tickets.</div> : (
          <ul className="max-h-96 divide-y divide-divider overflow-y-auto">
            {t.items.map((x) => (
              <li key={x.id}>
                <Link to={ticketPath(x.id)} className="block px-4 py-2 hover:bg-muted/40">
                  <div className="truncate text-sm text-foreground">{x.title}</div>
                  <div className="text-[11px] text-muted-foreground">{x.ticketNumber} · {x.statusLabel} · {x.priorityLabel} · {fmtRelative(x.createDate)}</div>
                </Link>
              </li>
            ))}
          </ul>
        )}
    </section>
  );
}

function Line({ label, value, tone }: { label: string; value: React.ReactNode; tone?: 'warn' | 'bad' }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-muted-foreground">{label}</span>
      <span className={cn('font-medium tabular-nums', tone === 'bad' ? 'text-destructive' : tone === 'warn' ? 'text-warning' : 'text-foreground')}>{value}</span>
    </div>
  );
}

/** The numbers you'd ask about first, each linking to where they come from. */
function Glance({ data }: { data: CompanyOrbit }) {
  const h = data.health;
  const id = data.company.id;
  const gapItems = (h.gaps ?? []).reduce((n, g) => n + g.items, 0);
  const critical = (h.gaps ?? []).filter((g) => g.severity === 'critical').reduce((n, g) => n + g.items, 0);
  const sec = h.security && !h.security.building ? h.security : null;
  const backupCov = h.backup?.find((b) => b.coveragePct != null)?.coveragePct ?? null;
  const renewSoon = h.csp?.renewals.filter((r) => r.days >= 0 && r.days <= 30).length ?? null;
  const tiles: { label: string; value: React.ReactNode; sub?: string; to: string; tone?: 'warn' | 'bad' | 'good' }[] = [
    { label: 'Open tickets', value: data.tickets.total != null ? (data.tickets.total >= 100 ? '100+' : data.tickets.total) : '—', sub: h.activity ? `+${h.activity.created30} / −${h.activity.completed30} in 30 d` : undefined, to: `${ROUTES.psa.tickets.search}?scope=${id}` },
    { label: 'Gaps', value: gapItems, sub: critical ? `${critical} critical` : 'none critical', to: insightsGapsView({ q: data.company.name }), tone: critical ? 'bad' : gapItems ? 'warn' : 'good' },
    ...(h.rmm ? [{ label: 'RMM alerts', value: h.rmm.openAlerts, sub: `${h.rmm.online}/${h.rmm.devices} online`, to: rmmAlertsView({ client: data.company.name }), tone: h.rmm.openAlerts ? 'warn' as const : 'good' as const }] : []),
    ...(h.rmm ? [{ label: 'Patching', value: h.rmm.patchPct != null ? `${h.rmm.patchPct}%` : '—', sub: 'compliant', to: `${ROUTES.rmm.dashboards.patching}?scope=${id}`, tone: h.rmm.patchPct == null ? undefined : h.rmm.patchPct < 75 ? 'bad' as const : h.rmm.patchPct < 90 ? 'warn' as const : 'good' as const }] : []),
    ...(sec ? [{ label: 'Secure score', value: sec.secureScorePct != null ? `${sec.secureScorePct}%` : '—', sub: sec.mfaRegisteredPct != null ? `MFA ${sec.mfaRegisteredPct}%` : sec.mfaUnknown ? 'MFA unknown' : undefined, to: cippTenantPath(sec.customerId), tone: sec.secureScorePct != null && sec.secureScorePct < 40 ? 'bad' as const : undefined }] : []),
    ...(backupCov != null ? [{ label: 'SaaS backup', value: `${backupCov}%`, sub: 'users covered', to: backupSaasCustomerPath(h.backup!.find((b) => b.coveragePct != null)!.saasCustomerId), tone: backupCov < 95 ? 'warn' as const : 'good' as const }] : []),
    ...(h.soc ? [{ label: 'SOC incidents', value: h.soc.open.length, sub: `${h.soc.incidents90} in 90 d`, to: `${ROUTES.soc.incidents}?scope=${id}`, tone: h.soc.open.length ? 'warn' as const : 'good' as const }] : []),
    ...(renewSoon != null ? [{ label: 'NCE renewals', value: renewSoon, sub: 'next 30 days', to: cspLicencesView({ client: data.company.csp?.customers[0] ?? data.company.name }) }] : []),
  ];
  return (
    <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4 min-[1400px]:grid-cols-8">
      {tiles.map((t) => (
        <Link key={t.label} to={t.to} className="rounded-lg border border-border bg-card px-3 py-2.5 shadow-sm transition-colors hover:border-ring/50 hover:bg-muted/30">
          <div className="truncate text-[10.5px] font-medium uppercase tracking-wider text-muted-foreground" title={t.label}>{t.label}</div>
          <div className={cn('text-xl font-semibold tabular-nums', t.tone === 'bad' ? 'text-destructive' : t.tone === 'warn' ? 'text-warning' : t.tone === 'good' ? 'text-success' : 'text-foreground')}>{t.value}</div>
          {t.sub && <div className="truncate text-[11px] text-muted-foreground" title={t.sub}>{t.sub}</div>}
        </Link>
      ))}
    </div>
  );
}

function SectionShell({ icon: Icon, title, more, children }: { icon: typeof Ticket; title: string; more?: { to: string; label: string }; children: React.ReactNode }) {
  return (
    <section className="rounded-lg border border-border bg-card shadow-sm">
      <div className="flex items-center justify-between gap-2 border-b border-divider px-4 py-2.5">
        <h2 className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"><Icon className="h-3.5 w-3.5" />{title}</h2>
        {more && <Link to={more.to} className="text-xs text-muted-foreground hover:text-foreground hover:underline">{more.label} →</Link>}
      </div>
      {children}
    </section>
  );
}

const SEV_TEXT: Record<string, string> = { critical: 'text-destructive', warning: 'text-warning', advisory: 'text-muted-foreground' };

function GapsSection({ health, companyName }: { health: OrbitHealth; companyName: string }) {
  const gaps = health.gaps ?? [];
  return (
    <SectionShell icon={AlertTriangle} title="Gaps" more={{ to: insightsGapsView({ q: companyName }), label: 'Gap list' }}>
      {health.gaps == null ? <div className="px-4 py-4 text-xs text-muted-foreground">Gaps unavailable.</div>
        : gaps.length === 0 ? <div className="px-4 py-4 text-xs text-success">No open gaps for this client.</div> : (
          <ul className="divide-y divide-divider">
            {gaps.map((g) => (
              <li key={g.kind}>
                <Link to={insightsGapsView({ kind: g.kind, q: companyName })} className="flex items-center justify-between gap-2 px-4 py-2 text-sm hover:bg-muted/40">
                  <span className={SEV_TEXT[g.severity]}>{g.label}</span>
                  <span className="text-xs text-muted-foreground"><span className="font-semibold tabular-nums text-foreground">{g.items}</span>{g.ticketed > 0 && ` · ${g.ticketed} ticketed`}</span>
                </Link>
              </li>
            ))}
          </ul>
        )}
    </SectionShell>
  );
}

const PRIO_TEXT: Record<string, string> = { Critical: 'text-destructive', High: 'text-destructive', Moderate: 'text-warning', Low: 'text-info' };

function AlertsSection({ health, companyName }: { health: OrbitHealth; companyName: string }) {
  const r = health.rmm;
  return (
    <SectionShell icon={MonitorSmartphone} title={`Worst RMM alerts${r ? ` · ${r.openAlerts} open` : ''}`} more={r ? { to: rmmAlertsView({ client: companyName }), label: 'All alerts' } : undefined}>
      {!r ? <div className="px-4 py-4 text-xs text-muted-foreground">Not in Datto RMM.</div>
        : r.topAlerts.length === 0 ? <div className="px-4 py-4 text-xs text-success">No open alerts above Information.</div> : (
          <ul className="divide-y divide-divider">
            {r.topAlerts.map((a) => (
              <li key={a.alertUid} className="px-4 py-2">
                <div className="flex items-center gap-2 text-sm">
                  <span className={cn('shrink-0 text-xs font-semibold', PRIO_TEXT[a.priority] ?? 'text-muted-foreground')}>{a.priority}</span>
                  <span className="truncate text-foreground" title={a.summary}>{a.summary}</span>
                </div>
                <div className="text-[11px] text-muted-foreground">
                  {a.deviceUid ? <Link to={rmmDevicePath(a.deviceUid)} className="hover:underline">{a.deviceName}</Link> : a.deviceName} · {fmtRelative(a.timestamp)}{a.ticketNumber && ` · ${a.ticketNumber}`}
                </div>
              </li>
            ))}
          </ul>
        )}
    </SectionShell>
  );
}

function ActivitySection({ health }: { health: OrbitHealth }) {
  const a = health.activity;
  return (
    <SectionShell icon={Activity} title={`Recent activity${a ? ` · ${a.created30} opened, ${a.completed30} closed (30 d)` : ''}`}>
      {!a ? <div className="px-4 py-4 text-xs text-muted-foreground">Couldn't load ticket activity.</div>
        : a.items.length === 0 ? <div className="px-4 py-4 text-xs text-muted-foreground">No tickets opened or closed in the last 30 days.</div> : (
          <ul className="max-h-96 divide-y divide-divider overflow-y-auto">
            {a.items.map((t) => (
              <li key={`${t.id}-${t.event}`}>
                <Link to={ticketPath(t.id)} className="block px-4 py-2 hover:bg-muted/40">
                  <div className="truncate text-sm text-foreground">{t.title}</div>
                  <div className="text-[11px] text-muted-foreground">
                    <span className={t.event === 'completed' ? 'text-success' : 'text-info'}>{t.event}</span> {fmtRelative(t.at)} · {t.ticketNumber} · {t.statusLabel}
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
    </SectionShell>
  );
}
